import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { smartAxios } from "../../api/axios/Axios";

export const ConnectedSuccessfully = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(queryParams.entries());

  // Detect platform based on the current pathname
  let platform = "Service";
  if (location.pathname.includes("instagram")) {
    platform = "Instagram";
  } else if (location.pathname.includes("whatsapp")) {
    platform = "WhatsApp";
  } else if (location.pathname.includes("calendar")) {
    platform = "Google Calendar";
  }

  // Detect connection status
  const hasError = !!params.error || params.param === "failure" || params.param === "error" || params.success === "false";
  const isSuccess = !hasError;
  const errorMsg = params.error || params.message || t("settings.somethingWentWrong");

  const handleGoToSettings = () => {
    // Fire request in the background
    smartAxios.get("/auth/social-profile-view/").catch(() => {});

    // Navigate to settings in the current tab
    navigate("/settings");
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center p-4 sm:p-6"
      style={{
        background:
          "radial-gradient(ellipse at center, #dde0ff 0%, #eef1ff 60%, #f4f5fa 100%)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative bg-white rounded-3xl shadow-2xl p-6 sm:p-8 max-w-lg w-full text-center overflow-hidden"
      >
        {/* Connection Animation / Status Icon */}
        {isSuccess ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              delay: 0.2,
              type: "spring",
              stiffness: 260,
              damping: 18,
            }}
            className="mx-auto mb-6 w-20 h-20 rounded-full bg-[#48bb60] flex items-center justify-center relative z-10"
            style={{ boxShadow: "0 8px 32px rgba(72,187,96,0.45)" }}
          >
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              viewBox="0 0 24 24"
            >
              <motion.path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.35, duration: 0.45, ease: "easeOut" }}
              />
            </svg>
          </motion.div>
        ) : (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              delay: 0.2,
              type: "spring",
              stiffness: 260,
              damping: 18,
            }}
            className="mx-auto mb-6 w-20 h-20 rounded-full bg-[#e53e3e] flex items-center justify-center relative z-10"
            style={{ boxShadow: "0 8px 32px rgba(229,62,62,0.45)" }}
          >
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              viewBox="0 0 24 24"
            >
              <motion.path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.35, duration: 0.45, ease: "easeOut" }}
              />
            </svg>
          </motion.div>
        )}

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-2 relative z-10">
          {isSuccess
            ? t("settings.connectSuccessTitle", { platform })
            : t("settings.connectFailedTitle", { platform })}
        </h2>
        <p className="text-gray-500 text-sm leading-relaxed mb-8 relative z-10">
          {isSuccess
            ? t("settings.connectSuccessDesc")
            : errorMsg}
        </p>

        {/* Actions */}
        <div className="relative z-10">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGoToSettings}
            className={`w-full py-3 rounded-xl text-white font-medium text-sm transition shadow-lg ${
              isSuccess
                ? "bg-[#020637] hover:bg-[#0e1c7a] shadow-[#020637]/10"
                : "bg-[#e53e3e] hover:bg-[#c53030] shadow-red-100"
            }`}
          >
            {isSuccess ? t("settings.goToSettings") : t("settings.tryAgain")}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default ConnectedSuccessfully;
